import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import Layout from "../components/Layout";

const EMPTY_PROJECT = { title: "", tagline: "", description: "", status: "active", videos: [], pdf_url: "", tutorials: [], link: "", order: 0, logo: "", future: false, tag: "", sections: [], video_banner: null, cta_url: "", cta_label: "" };

const sectionsToText = (arr) => (Array.isArray(arr) ? arr : []).map((s) => (s.title || "") + "\n\n" + (s.body || "")).join("\n---\n");
const textToSections = (txt) => String(txt || "").split("\n---\n").map((blk) => { const parts = blk.trim().split("\n\n"); const title = (parts.shift() || "").trim(); const body = parts.join("\n\n").trim(); return { title, body }; }).filter((s) => s.title || s.body);
const EMPTY_NEWS = { title: "", body: "", image_url: "" };

export default function Admin() {
  const { isAdmin, loading } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState("projects");

  if (loading) return null;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <h1 className="font-mono font-extrabold text-3xl mb-8">{t("admin_title")}</h1>
        <div className="flex gap-2 mb-10 border-b border-white/10">
          {[
            ["projects", t("tab_projects")],
            ["news", t("tab_news")],
            ["members", t("tab_members")],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => setTab(k)}
              className={`font-mono text-sm px-4 py-3 -mb-px border-b-2 transition-colors ${
                tab === k ? "border-nhgreen text-nhgreen" : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "projects" && <ProjectsAdmin t={t} />}
        {tab === "news" && <NewsAdmin t={t} />}
        {tab === "members" && <MembersAdmin t={t} />}
      </div>
    </Layout>
  );
}

function ProjectsAdmin({ t }) {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const openEdit = async (p) => {
    if (p) {
      const { data } = await api.get(`/projects/${p.id}`);
      setEditing(data);
    } else {
      setEditing({ ...EMPTY_PROJECT });
    }
  };

  const save = async () => {
    try {
      const payload = { ...editing };
      if (typeof payload.videos === "string") payload.videos = payload.videos.split("\n").map((s) => s.trim()).filter(Boolean);
      if (typeof payload.sections === "string") payload.sections = textToSections(payload.sections);
      if (payload.video_banner && !payload.video_banner.url) payload.video_banner = null;
      if (editing.id) await api.put(`/admin/projects/${editing.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success("Sačuvano.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Obrisati projekat?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Obrisano.");
    load();
  };

  return (
    <div>
      <button data-testid="admin-add-project" onClick={() => openEdit(null)} className="flex items-center gap-2 font-mono font-bold text-sm bg-nhgreen text-black rounded-md px-4 py-2.5 mb-6 hover:bg-[#1ee068] transition-colors">
        <Plus className="w-4 h-4" /> {t("add_project")}
      </button>
      <div className="space-y-3">
        {projects.map((p) => (
          <div key={p.id} data-testid={`admin-project-${p.id}`} className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-xl px-5 py-4">
            <div>
              <div className="font-mono font-bold">{p.title}</div>
              <div className="text-white/40 text-xs mt-1 font-mono">{p.status}</div>
            </div>
            <div className="flex gap-2">
              <IconBtn onClick={() => openEdit(p)} testid={`admin-edit-project-${p.id}`}><Pencil className="w-4 h-4" /></IconBtn>
              <IconBtn onClick={() => remove(p.id)} testid={`admin-delete-project-${p.id}`} danger><Trash2 className="w-4 h-4" /></IconBtn>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? t("edit") : t("add_project")}>
          <TInput label="Naziv" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="project-form-title" />
          <TArea label="Kratak opis (opis)" value={editing.tagline} onChange={(v) => setEditing({ ...editing, tagline: v })} testid="project-form-tagline" />
          <TArea label="Detaljan opis" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="project-form-description" />
          <label className="block mb-4">
            <span className="font-mono text-xs uppercase text-white/50">Status</span>
            <select data-testid="project-form-status" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="mt-2 w-full bg-[#111] border border-white/20 rounded-md px-4 py-3 text-sm font-mono">
              <option value="active">active</option>
              <option value="u pripremi">u pripremi</option>
            </select>
          </label>
          <TArea label="Video URL-ovi (jedan po liniji)" value={Array.isArray(editing.videos) ? editing.videos.join("\n") : editing.videos} onChange={(v) => setEditing({ ...editing, videos: v })} testid="project-form-videos" />
          <TInput label="PDF URL" value={editing.pdf_url} onChange={(v) => setEditing({ ...editing, pdf_url: v })} testid="project-form-pdf" />
          <TInput label="Eksterni link" value={editing.link} onChange={(v) => setEditing({ ...editing, link: v })} testid="project-form-link" />
          <TInput label="Dugme — tekst (npr. Pridruzi se projektu)" value={editing.cta_label || ""} onChange={(v) => setEditing({ ...editing, cta_label: v })} testid="project-form-cta-label" />
          <TInput label="Dugme — link (Google forma)" value={editing.cta_url || ""} onChange={(v) => setEditing({ ...editing, cta_url: v })} testid="project-form-cta-url" />
          <TInput label="Logo URL (npr. /logos/rift.png)" value={editing.logo || ""} onChange={(v) => setEditing({ ...editing, logo: v })} testid="project-form-logo" />
          <TInput label="Tag (mali natpis iznad naslova)" value={editing.tag || ""} onChange={(v) => setEditing({ ...editing, tag: v })} testid="project-form-tag" />
          <TInput label="Baner — naslov" value={(editing.video_banner || {}).title || ""} onChange={(v) => setEditing({ ...editing, video_banner: { ...(editing.video_banner || {}), title: v } })} testid="project-form-banner-title" />
          <TInput label="Baner — podnaslov" value={(editing.video_banner || {}).subtitle || ""} onChange={(v) => setEditing({ ...editing, video_banner: { ...(editing.video_banner || {}), subtitle: v } })} testid="project-form-banner-subtitle" />
          <TInput label="Baner — video link" value={(editing.video_banner || {}).url || ""} onChange={(v) => setEditing({ ...editing, video_banner: { ...(editing.video_banner || {}), url: v } })} testid="project-form-banner-url" />
          <TArea label={"Sekcije (naslov, prazan red, tekst; izmedju sekcija red sa ---)"} value={typeof editing.sections === "string" ? editing.sections : sectionsToText(editing.sections)} onChange={(v) => setEditing({ ...editing, sections: v })} testid="project-form-sections" />
          <div className="flex gap-3 mt-6">
            <button data-testid="project-form-save" onClick={save} className="flex-1 font-mono font-bold text-sm bg-nhgreen text-black rounded-md py-3">{t("save")}</button>
            <button onClick={() => setEditing(null)} className="flex-1 font-mono text-sm border border-white/20 rounded-md py-3">{t("cancel")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function NewsAdmin({ t }) {
  const [news, setNews] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/news").then((r) => setNews(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/news/${editing.id}`, editing);
      else await api.post("/admin/news", editing);
      toast.success("Sačuvano.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Obrisati novost?")) return;
    await api.delete(`/admin/news/${id}`);
    load();
  };

  return (
    <div>
      <button data-testid="admin-add-news" onClick={() => setEditing({ ...EMPTY_NEWS })} className="flex items-center gap-2 font-mono font-bold text-sm bg-nhgreen text-black rounded-md px-4 py-2.5 mb-6 hover:bg-[#1ee068] transition-colors">
        <Plus className="w-4 h-4" /> {t("add_news")}
      </button>
      <div className="space-y-3">
        {news.map((n) => (
          <div key={n.id} data-testid={`admin-news-${n.id}`} className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-xl px-5 py-4">
            <div className="font-mono font-bold">{n.title}</div>
            <div className="flex gap-2">
              <IconBtn onClick={() => setEditing(n)}><Pencil className="w-4 h-4" /></IconBtn>
              <IconBtn onClick={() => remove(n.id)} danger><Trash2 className="w-4 h-4" /></IconBtn>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? t("edit") : t("add_news")}>
          <TInput label="Naslov" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="news-form-title" />
          <TArea label="Tekst" value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} testid="news-form-body" />
          <TInput label="Slika URL (opciono)" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} testid="news-form-image" />
          <div className="flex gap-3 mt-6">
            <button data-testid="news-form-save" onClick={save} className="flex-1 font-mono font-bold text-sm bg-nhgreen text-black rounded-md py-3">{t("save")}</button>
            <button onClick={() => setEditing(null)} className="flex-1 font-mono text-sm border border-white/20 rounded-md py-3">{t("cancel")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function MembersAdmin({ t }) {
  const [members, setMembers] = useState([]);
  useEffect(() => { api.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);
  return (
    <div className="overflow-x-auto" data-testid="admin-members-table">
      <table className="w-full text-left font-mono text-sm">
        <thead>
          <tr className="text-white/40 text-xs uppercase tracking-widest border-b border-white/10">
            <th className="py-3 pr-4">{t("first_name")}</th>
            <th className="py-3 pr-4">{t("last_name")}</th>
            <th className="py-3 pr-4">{t("email")}</th>
            <th className="py-3 pr-4">Role</th>
            <th className="py-3">{t("joined")}</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id} className="border-b border-white/5">
              <td className="py-3 pr-4">{m.first_name}</td>
              <td className="py-3 pr-4">{m.last_name}</td>
              <td className="py-3 pr-4 text-white/70">{m.email}</td>
              <td className="py-3 pr-4">
                <span className={m.role === "admin" ? "text-nhorange" : "text-nhgreen"}>{m.role}</span>
              </td>
              <td className="py-3 text-white/50">{m.created_at ? new Date(m.created_at).toLocaleDateString() : "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IconBtn({ children, onClick, danger, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`p-2 rounded-md border border-white/15 transition-colors ${danger ? "hover:border-nhorange hover:text-nhorange" : "hover:border-nhgreen hover:text-nhgreen"}`}>
      {children}
    </button>
  );
}
function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#0A0A0A] border border-white/15 rounded-2xl p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-mono font-bold text-xl">{title}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function TInput({ label, value, onChange, testid }) {
  return (
    <label className="block mb-4">
      <span className="font-mono text-xs uppercase text-white/50">{label}</span>
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full bg-[#111] border border-white/20 rounded-md px-4 py-3 text-sm outline-none focus:border-nhgreen" />
    </label>
  );
}
function TArea({ label, value, onChange, testid }) {
  return (
    <label className="block mb-4">
      <span className="font-mono text-xs uppercase text-white/50">{label}</span>
      <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="mt-2 w-full bg-[#111] border border-white/20 rounded-md px-4 py-3 text-sm outline-none focus:border-nhgreen resize-y" />
    </label>
  );
}
